import React, { ReactNode, useEffect, useState } from 'react';
import classNames                                from 'classnames';

import { ENTITY_COLORIZATION, ENTITY_ICONS }              from '@src/constants';
import { ProjectService, ProjectTimelineDateFieldObject } from '@src/services/ProjectService';
import { ProjectMilestoneIcon }                           from '@src/views/components/project/ProjectMilestoneIcon';
import { useQuery }                                       from "@apollo/react-hooks";
import { ProjectQueries }                                 from "@src/queries";
import { queriesHaveData, queriesLoading }                from "@src/store/_apolloUtil";
import _                                                  from "underscore";
import { Uuid }                                           from "@src/types";
import { MilestoneDateIconProps }                         from "@src/views/components/project/MilestoneDateIcon";


type UseProjectMilestoneHookMethod = ( projectId?: Uuid ) => UseProjectMilestoneHook;

export type UseProjectMilestoneHook = {
    getTimelineFieldIcon: ( field: ProjectTimelineDateFieldObject, iconProps: Omit<MilestoneDateIconProps, 'desc' | 'label' | 'date'>, useColor?: boolean, useIcon?: boolean ) => ReactNode,
    getTimelineFieldIconProperties: ( icon?: string, color?: string ) => {
        hasIcon: boolean,
        hasColor: boolean,
        iconName: string,
        iconColor: string,
        cssValue: string,
        alphaHexColor: string,
        iconClasses: string
    },
    getTimelineFieldForProjectMilestone: ( id: Uuid ) => ProjectTimelineDateFieldObject | null,
    getTimelineFieldObjectsForProject: () => ProjectTimelineDateFieldObject[] | [],
};

export const useProjectMilestone: UseProjectMilestoneHookMethod = ( projectId = null ) => {

    const [ projectMilestones, setProjectMilestones ] = useState<ProjectTimelineDateFieldObject[]>( [] );

    const getProjectMilestoneReq = useQuery(
        ProjectQueries.queries.GET_PROJECT( `
                id 
                ${ ProjectQueries.fragments.PROJECTS.TIMELINE_BOUNDARY_FIELDS() }
                ${ ProjectQueries.fragments.PROJECTS.DEFINED_TIMING_FIELDS() }
            ` ), {
            variables:   { id: projectId },
            fetchPolicy: 'cache-and-network',
            skip:        !projectId
        } );

    useEffect( () => {

        if( queriesHaveData( getProjectMilestoneReq ) && queriesHaveData( getProjectMilestoneReq ) ){

            const project = _.first( getProjectMilestoneReq.data.projects );

            setProjectMilestones( ProjectService.getTimelineDataObjectFromProject( project ) )
        }

    }, [ getProjectMilestoneReq ] );

    const getTimelineFieldIcon: UseProjectMilestoneHook['getTimelineFieldIcon'] = ( field, otherProps, useColor = true, useIcon = false ) => {

        let iconProp = _.extend( otherProps?.icon ?? {}, {
            color:     useColor ? field.color : ENTITY_COLORIZATION.MILESTONES.DEFAULT,
            name:      useIcon ? field.icon : ENTITY_ICONS.MILESTONES.ITEM,
            className: classNames( otherProps?.icon?.className, {
                'rotate--45': !useIcon,
            } )
        } );

        return (

            <ProjectMilestoneIcon
                key={ field.fieldId }
                label={ field.label }
                date={ field.value }
                desc={ field.description }
                icon={ iconProp as any }
                { ..._.omit( otherProps, [ 'icon' ] ) }
            />

        )
    }

    const getTimelineFieldIconProperties: UseProjectMilestoneHook['getTimelineFieldIconProperties'] = ( icon, color ) => {

        return ProjectService.getMilestoneVisualProperties( icon, color );
    }

    const getTimelineFieldForProjectMilestone: UseProjectMilestoneHook['getTimelineFieldForProjectMilestone'] = ( milestoneId ) => {

        return milestoneId ? _.findWhere( projectMilestones, { id: milestoneId } ) ?? null : null;
    }

    const getTimelineFieldObjectsForProject: UseProjectMilestoneHook['getTimelineFieldObjectsForProject'] = () => {

        return projectMilestones ?? [];
    }

    return {
        getTimelineFieldIcon,
        getTimelineFieldForProjectMilestone,
        getTimelineFieldObjectsForProject,
        getTimelineFieldIconProperties,
    };
};
